/**
 * @file blendSolver.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-17
 * @description Least-cost formulation: the cheapest blend that still meets the
 *              spec, stated as a linear program and solved as one.
 *
 *              The variables are the inclusions, the objective is Σ pᵢxᵢ/100,
 *              and every constraint is linear in exactly the same way — the
 *              total is Σxᵢ = 100, each nutrient is minₖ ≤ Σnᵢₖxᵢ/100 ≤ maxₖ,
 *              and each material sits inside its own bounds. Nothing here is
 *              approximated, so "no blend meets this spec" comes back as a
 *              proof from phase 1 rather than as a solver giving up.
 *
 *              Two refusals are deliberate and both are about not quoting a
 *              number that isn't real:
 *
 *              A material with no price in the chosen column cannot be a
 *              candidate. Treating a missing price as zero makes it free, and
 *              a least-cost solver asked for free ingredients will use nothing
 *              else. It is excluded and named.
 *
 *              A material with no figure for a nutrient that is being
 *              constrained cannot be a candidate either. Assuming zero
 *              understates that nutrient, which is merely wasteful against a
 *              minimum but unsafe against a maximum — the solver would happily
 *              load up on an unmeasured material and report a maximum as met.
 *              Also excluded, also named, so the fix (fill in the figure) is
 *              obvious.
 *
 *              Locked materials are held exactly where the user put them. That
 *              is the whole contract of the lock everywhere else in the
 *              calculator, and a solver that quietly moved them would make the
 *              lock mean two different things on one screen.
 *
 *              RULES THAT ARE NOT ABOUT AMOUNTS (1.2.0)
 *
 *              "Under £250 a tonne" is linear — one more row, Σ pᵢxᵢ/100 ≤ 250.
 *              "At least four ingredients", "no more than seven" and "a genuinely
 *              different blend" are not: whether a material is in the blend is a
 *              yes-or-no fact. Those go through blendSearch.js, a branch and
 *              bound that decides which materials are in and solves this same LP
 *              at every step, so the answer is still the proved cheapest — to
 *              the stated rules, not to an approximation of them.
 *
 *              NO DEFAULT MINIMUM INCLUSION (1.3.0)
 *
 *              A counted ingredient used to have to be at least 0.5% of the
 *              blend. It no longer does: anything the blend actually contains
 *              counts, down to the technical floor in blendInclusion.js (the
 *              smallest inclusion that shows and prints). A minimum is only what
 *              the request states — `minInclusion`, "each ingredient at least
 *              1%" — and a stated one is kept whether or not ingredients are
 *              being counted: every material in the answer is at that figure or
 *              more, or absent.
 */

// Explicit extensions: webpack does not need them, but scripts/check-blend-solver.mjs
// imports this module straight into bare node, which does.
import { solveLP } from './lpSolve.js';
import { searchSupport, SEARCH_LIMITS } from './blendSearch.js';
import * as blendMath from './blendMath.js';
import {
  INCLUSION_DECIMALS, INCLUSION_FLOOR, MAX_MIN_INCLUSION, countIngredients, formatInclusion
} from './blendInclusion.js';

// Re-exported: callers have always found these beside the solver.
export { INCLUSION_FLOOR, countIngredients, isIncluded } from './blendInclusion.js';

/** Inclusions are percentages to two decimals — see blendInclusion.js. */
const DECIMALS = INCLUSION_DECIMALS;

/** Safety margins tried in turn — see attempt() below. */
const MARGINS = [0, 0.005, 0.02];

/**
 * £ of headroom on a cost ceiling per unit of nutrient margin: £0, £0.05, £0.20.
 * Rounding each inclusion to hundredths moves a blend's cost by pennies, and a
 * ceiling needs the same room to land in as a nutrient floor does.
 */
const COST_GIVE_PER_MARGIN = 10;

/** A cost is under its ceiling to within half a penny. */
export const COST_TOLERANCE = 0.005;

const EPS = 1e-7;

const positiveInt = (v) => {
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isInteger(n) && n > 0 ? n : null;
};

const positiveNumber = (v) => {
  const n = blendMath.toNumber(v);
  return n !== null && n > 0 ? n : null;
};

/** A stated minimum inclusion, or null — out of range is "not asked for". */
const statedMinimum = (v) => {
  const n = positiveNumber(v);
  return n !== null && n <= MAX_MIN_INCLUSION ? n : null;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The rules in words — "at least 4 ingredients, each ingredient at least 1% and
 * under £250.00/T". Used in every refusal, so a refusal always says which rule
 * it could not keep.
 */
export function describeRules({ minIngredients, maxIngredients, costMax, minInclusion, differentFrom } = {}) {
  const min = positiveInt(minIngredients);
  const max = positiveInt(maxIngredients);
  const ceiling = positiveNumber(costMax);
  const floor = statedMinimum(minInclusion);
  const parts = [];
  if (min && max) {
    parts.push(min === max ? `exactly ${plural(min, 'ingredient')}` : `between ${min} and ${max} ingredients`);
  } else if (min) {
    parts.push(`at least ${plural(min, 'ingredient')}`);
  } else if (max) {
    parts.push(`no more than ${plural(max, 'ingredient')}`);
  }
  if (floor !== null) parts.push(`each ingredient at least ${formatInclusion(floor)}%`);
  if (ceiling !== null) parts.push(`under £${ceiling.toFixed(2)}/T`);
  if (differentFrom && differentFrom.length) parts.push('different from the blends before it');
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** A refusal that names the rule it could not keep. */
function ruleRefusal(status, rules, nodes, fallback) {
  const said = describeRules(rules);
  if (status === 'limit') {
    return {
      status: 'search-limit',
      message: `Couldn't find a blend with ${said} before the search limit (${nodes} combinations tried). `
        + 'Loosen a rule, or name the materials to use.'
    };
  }
  if (status === 'infeasible') {
    return {
      status: 'infeasible',
      message: said
        ? `Couldn't find a blend of these materials that meets the spec with ${said}.`
        : fallback
    };
  }
  return { status, message: 'The solver could not settle on an answer.' };
}

/**
 * @param {Object}   opts
 * @param {Object}   opts.book         from blendMath.buildBook
 * @param {Object}   opts.values       current { materialKey: inclusion }
 * @param {string[]} opts.locked       keys held exactly as they are
 * @param {string[]} opts.candidates   keys the solver may use
 * @param {string}   opts.columnKey    price column to minimise against
 * @param {Object}   [opts.spec]       { targets: { componentKey: {min,max} } }
 * @param {Object}   [opts.bounds]     { materialKey: {min,max} } inclusion limits
 * @param {number}   [opts.totalTarget]
 * @param {number}   [opts.minIngredients]  fewest materials in the blend
 * @param {number}   [opts.maxIngredients]  most materials in the blend
 * @param {number}   [opts.costMax]         £/T ceiling on the raw material cost
 * @param {number}   [opts.minInclusion]    the least any material in the answer may be,
 *                                          when the request stated one (%). Unset:
 *                                          no minimum beyond INCLUSION_FLOOR
 * @param {string[][]} [opts.differentFrom] each a set of materials at least one
 *                                          of which must be left out
 * @param {Object}   [opts.limits]          { nodes, ms, deadline } for the search
 *
 * @returns {{status, values, cost, previousCost, excluded, binding, message,
 *            proved, nodes, ingredients}}
 */
export function solveBlend({
  book,
  values,
  locked = [],
  candidates,
  columnKey,
  spec = null,
  bounds = {},
  totalTarget = 100,
  dropZeros = false,
  minIngredients = null,
  maxIngredients = null,
  costMax = null,
  minInclusion = null,
  differentFrom = [],
  limits = {}
}) {
  const lockedSet = new Set(locked);
  const targets = (spec && spec.targets) || {};
  const constrained = Object.entries(targets)
    .filter(([, t]) => blendMath.toNumber(t?.min) !== null || blendMath.toNumber(t?.max) !== null)
    .map(([key]) => key);

  const minCount = positiveInt(minIngredients);
  const maxCount = positiveInt(maxIngredients);
  const ceiling = positiveNumber(costMax);
  const cuts = (differentFrom || []).filter((c) => Array.isArray(c) && c.length);
  const statedFloor = statedMinimum(minInclusion);
  const rules = {
    minIngredients: minCount, maxIngredients: maxCount, costMax: ceiling,
    minInclusion: statedFloor, differentFrom: cuts
  };
  /*
   * Whether in-or-out is a decision at all, and the least "in" can be.
   *
   * Counting needs a floor so a material is not "used" at 1e-9 of a percent; a
   * stated minimum IS a floor. Either way it is never below INCLUSION_FLOOR, the
   * smallest inclusion that shows and prints — and with neither, there is none.
   */
  const counting = !!(minCount || maxCount);
  const floored = counting || statedFloor !== null;
  const floorAt = Math.max(INCLUSION_FLOOR, statedFloor ?? 0);

  const keys = candidates && candidates.length
    ? candidates
    : Object.keys(values || {});

  // ── Who can take part ─────────────────────────────────────────────────────
  const free = [];
  const excluded = [];
  for (const key of keys) {
    if (lockedSet.has(key)) continue;
    const material = book.materials.get(key);
    if (!material) {
      excluded.push({ key, name: key, reason: 'unknown', detail: 'Not in the cost book' });
      continue;
    }
    const price = material.costs[columnKey];
    if (price === undefined || price === null) {
      excluded.push({ key, name: material.name, reason: 'no-price',
                      detail: 'No price in this column' });
      continue;
    }
    const missing = constrained.filter((c) => material.nutrients[c] === undefined);
    if (missing.length) {
      excluded.push({ key, name: material.name, reason: 'no-nutrient',
                      detail: `No figure for ${missing.join(', ')}` });
      continue;
    }
    free.push({ key, material, price });
  }

  if (!free.length) {
    return {
      status: 'no-candidates', values: null, excluded, rules,
      message: 'Nothing can be solved with — every candidate is locked, unpriced, or missing a figure the spec constrains.'
    };
  }

  // ── What the locked materials already contribute ──────────────────────────
  let fixedInclusion = 0;
  let fixedCost = 0;
  let fixedCount = 0;
  const fixedNutrient = {};
  for (const key of lockedSet) {
    const value = blendMath.toNumber(values[key]);
    if (!value) continue;
    fixedInclusion += value;
    fixedCount += 1;
    const material = book.materials.get(key);
    if (!material) continue;
    const price = blendMath.toNumber(material.costs[columnKey]);
    if (price !== null) fixedCost += (value * price) / 100;
    for (const c of constrained) {
      const n = material.nutrients[c];
      if (n !== undefined) fixedNutrient[c] = (fixedNutrient[c] || 0) + value * n;
    }
  }

  const room = totalTarget - fixedInclusion;
  if (room < -1e-6) {
    return {
      status: 'over-locked', values: null, excluded, rules,
      message: `The locked materials already come to ${fixedInclusion.toFixed(2)}%. Unlock something before solving.`
    };
  }

  // ── Shift to y = x - lo so every variable starts at zero ───────────────────
  // The simplex wants x >= 0. A lower bound is handled by substitution rather
  // than as a row, which keeps the tableau to the constraints that matter.
  const ownLo = free.map(f => Math.max(0, blendMath.toNumber(bounds[f.key]?.min) ?? 0));
  const hi = free.map((f, i) => {
    const raw = blendMath.toNumber(bounds[f.key]?.max);
    return raw === null ? totalTarget : Math.max(ownLo[i], raw);
  });
  // A material with a lower bound of its own ("minimum 2% molasses", a named
  // material) is in whatever happens, so the floor applies to it directly — up
  // to its own ceiling, which is the more specific instruction.
  const lo = ownLo.map((v, i) => (floored && v > EPS ? Math.min(Math.max(v, floorAt), hi[i]) : v));

  const loSum = lo.reduce((s, v) => s + v, 0);
  if (loSum - room > 1e-6) {
    return {
      status: 'infeasible', values: null, excluded, rules,
      message: `The minimum inclusions add up to ${(loSum + fixedInclusion).toFixed(2)}%, which is already over ${totalTarget}%.`
    };
  }

  const allItems = free.map((f, i) => ({ f, lo: lo[i], hi: hi[i] }));

  /**
   * The LP over a chosen set of materials, each with its own bounds.
   *
   * Everything the solver ever solves goes through here — the plain least-cost
   * answer is this over every candidate, and each step of the ingredient search
   * is this over the materials still allowed, with some pushed up to a floor.
   */
  const solveItems = (items, margin) => {
    const n = items.length;
    if (!n) {
      return { status: room <= 1e-9 ? 'optimal' : 'infeasible', x: [] };
    }
    const itemsLoSum = items.reduce((s, it) => s + it.lo, 0);
    const A = [];
    const b = [];
    const ops = [];

    // Total.
    A.push(new Array(n).fill(1));
    b.push(room - itemsLoSum);
    ops.push('=');

    // Upper bounds, one row each. Only where the bound actually bites — a
    // material allowed the full 100% is not constrained by saying so.
    for (let i = 0; i < n; i += 1) {
      const span = items[i].hi - items[i].lo;
      if (span >= totalTarget - 1e-9) continue;
      const row = new Array(n).fill(0);
      row[i] = 1;
      A.push(row);
      b.push(span);
      ops.push('<=');
    }

    // Nutrients. Values are Σ nᵢₖxᵢ / 100, so the /100 goes into the row.
    for (const c of constrained) {
      const coefficients = items.map(it => (it.f.material.nutrients[c] || 0) / 100);
      const baseline = (fixedNutrient[c] || 0) / 100
        + coefficients.reduce((s, coef, i) => s + coef * items[i].lo, 0);

      const min = blendMath.toNumber(targets[c]?.min);
      const max = blendMath.toNumber(targets[c]?.max);
      // Never let the margin invert a band that is already tight.
      const band = min !== null && max !== null ? Math.max(0, (max - min) / 2) : Infinity;
      const give = Math.min(margin, band);

      if (min !== null) {
        A.push(coefficients.slice()); b.push(min + give - baseline); ops.push('>=');
      }
      if (max !== null) {
        A.push(coefficients.slice()); b.push(max - give - baseline); ops.push('<=');
      }
    }

    // The cost ceiling: one more linear row, pulled in by the margin for the
    // same reason the nutrient rows are.
    if (ceiling !== null) {
      const perTonne = items.map(it => it.f.price / 100);
      const baseline = fixedCost + perTonne.reduce((s, p, i) => s + p * items[i].lo, 0);
      A.push(perTonne);
      b.push(ceiling - margin * COST_GIVE_PER_MARGIN - baseline);
      ops.push('<=');
    }

    const objective = items.map(it => it.f.price / 100);
    return solveLP(objective, A, b, ops);
  };

  // ── Which materials are in, when that is itself a rule ────────────────────
  let items = allItems;
  let search = null;

  if (floored || cuts.length) {
    const byKey = new Map(allItems.map((it) => [it.f.key, it]));
    // NOT capped at the material's ceiling: one that cannot reach the floor
    // cannot be in the blend, and forcing it in comes back infeasible, which the
    // search reads as "leave it out". (Mandatory materials were floored above.)
    const floorOf = (key) => {
      if (!floored) return 0;
      const it = byKey.get(key);
      return it ? Math.max(it.lo, floorAt) : 0;
    };

    const solveNode = ({ forcedIn, forcedOut }) => {
      const nodeItems = allItems
        .filter((it) => !forcedOut.has(it.f.key))
        .map((it) => (forcedIn.has(it.f.key) ? { ...it, lo: Math.max(it.lo, floorOf(it.f.key)) } : it));
      const result = solveItems(nodeItems, 0);
      if (result.status !== 'optimal') return { status: result.status };
      const x = {};
      let cost = fixedCost;
      nodeItems.forEach((it, i) => {
        const v = it.lo + result.x[i];
        x[it.f.key] = v;
        cost += (it.f.price * v) / 100;
      });
      return { status: 'optimal', x, cost };
    };

    const ms = limits.ms ?? SEARCH_LIMITS.ms;
    search = searchSupport({
      keys: allItems.map((it) => it.f.key),
      solve: solveNode,
      mandatory: allItems.filter((it) => it.lo > EPS).map((it) => it.f.key),
      fixedCount,
      minCount,
      maxCount,
      floorOf,
      cuts,
      nodeLimit: limits.nodes ?? SEARCH_LIMITS.nodes,
      deadline: Math.min(limits.deadline ?? Infinity, Date.now() + ms)
    });

    if (search.status !== 'optimal') {
      const refusal = ruleRefusal(search.status, rules, search.nodes,
        'No blend of these materials meets the spec. Widen a limit, unlock a material, or add one that carries what is short.');
      return { ...refusal, values: null, excluded, rules, nodes: search.nodes };
    }

    // The search settled WHICH materials. The answer itself is solved again over
    // exactly those, each held at its floor, so the usual rounding and margins
    // apply and the count cannot change on the way out.
    const support = new Set(search.best.support);
    items = allItems
      .filter((it) => support.has(it.f.key))
      .map((it) => (it.lo > EPS ? it : { ...it, lo: floorOf(it.f.key) }));
  }

  /**
   * Build and solve at a given safety margin, then round.
   *
   * The margin exists because the answer is rounded to hundredths afterwards.
   * An LP sitting exactly on "CP >= 18" comes back as 18.0000, and rounding the
   * inclusions moves it to 17.9996 — which evaluateSpec correctly calls a fail.
   * Handing the user a solved blend that its own spec panel marks red is worse
   * than useless, so the constraint is pulled in by a hair and the rounding has
   * somewhere to land.
   */
  const attempt = (margin) => {
    const result = solveItems(items, margin);
    if (result.status !== 'optimal') return { result };

    const solved = {};
    for (const key of Object.keys(values || {})) {
      if (lockedSet.has(key)) solved[key] = blendMath.toNumber(values[key]) ?? 0;
    }
    items.forEach((it, i) => { solved[it.f.key] = it.lo + result.x[i]; });
    // A candidate the solver dropped to zero stays in the grid at zero rather
    // than disappearing — the user chose to have it there, and its absence from
    // the answer is itself information.
    //
    // `dropZeros` turns that off. It exists for the ⌘K builder, where the
    // candidate set is the whole book rather than a list somebody assembled: a
    // suggestion that came back with four materials and 37 zeroes would fill the
    // grid with rows nobody asked for, and 0 is not the same as "not in this
    // blend" once it is sitting in an editable cell.
    if (!dropZeros) {
      for (const key of keys) if (!(key in solved)) solved[key] = 0;
    } else {
      for (const key of Object.keys(solved)) {
        if (!lockedSet.has(key) && (blendMath.toNumber(solved[key]) || 0) <= 0) delete solved[key];
      }
    }

    const headroom = {};
    const floors = {};
    items.forEach((it) => { headroom[it.f.key] = it.hi; floors[it.f.key] = it.lo; });
    return { result, margin, values: roundToTotal(solved, lockedSet, totalTarget, headroom, floors) };
  };

  // Zero first: an untightened answer is the true least cost, and most specs
  // have enough slack that rounding never touches them. The margins escalate
  // only when the rounded blend actually fails its own spec or its ceiling.
  let attempted = null;
  for (const margin of MARGINS) {
    const candidate = attempt(margin);
    if (candidate.result.status !== 'optimal') {
      // A tightened problem going infeasible means the spec is exactly tight;
      // keep the untightened answer rather than reporting a false impossibility.
      if (attempted) break;
      attempted = candidate;
      break;
    }
    attempted = candidate;
    const specOk = !spec
      || blendMath.evaluateSpec(blendMath.nutrients(candidate.values, book).values, spec).pass;
    const costOk = ceiling === null
      || blendMath.cost(candidate.values, book, columnKey).cost <= ceiling + COST_TOLERANCE;
    if (specOk && costOk) break;
  }

  const { result, margin: usedMargin, values: rounded } = attempted;

  if (result.status === 'infeasible') {
    return {
      status: 'infeasible', values: null, excluded, rules,
      message: ceiling !== null
        ? `No blend of these materials meets the spec under £${ceiling.toFixed(2)}/T. Raise the ceiling, widen a limit, or add a material that carries what is short.`
        : 'No blend of these materials meets the spec. Widen a limit, unlock a material, or add one that carries what is short.'
    };
  }
  if (result.status !== 'optimal') {
    return {
      status: result.status, values: null, excluded, rules,
      message: result.status === 'unbounded'
        ? 'The problem is unbounded, which means a constraint is missing.'
        : 'The solver ran out of iterations without settling.'
    };
  }

  return {
    status: 'optimal',
    values: rounded,
    excluded,
    // Priced from the rounded answer, so the figure quoted is the figure the
    // blend on screen actually costs.
    cost: blendMath.cost(rounded, book, columnKey).cost,
    previousCost: blendMath.cost(values, book, columnKey).cost,
    binding: bindingConstraints(rounded, book, targets, constrained, usedMargin || 0),
    rules,
    // False only when the ingredient search stopped at its limit with an answer
    // in hand: every rule is kept, but a cheaper blend might exist — by no more
    // than `searchGap` £/T.
    proved: search ? search.proved : true,
    searchGap: search && !search.proved && search.lowerBound !== null
      ? Math.max(0, blendMath.cost(rounded, book, columnKey).cost - search.lowerBound) : 0,
    nodes: search ? search.nodes : 0,
    ingredients: countIngredients(rounded),
    message: null
  };
}

/**
 * Round to two decimals without breaking the total.
 *
 * Rounding each inclusion independently leaves the blend at 99.99 or 100.01,
 * and a solver that hands back an unbalanced blend has undone the one thing it
 * was asked for. The residue goes on the largest unlocked material, where a
 * hundredth of a percent is invisible.
 */
function roundToTotal(values, lockedSet, totalTarget, headroom = {}, floors = {}) {
  const out = {};
  const factor = 10 ** DECIMALS;
  for (const [key, value] of Object.entries(values)) {
    out[key] = Math.round((value || 0) * factor) / factor;
  }
  const sum = Object.values(out).reduce((s, v) => s + v, 0);
  const drift = Math.round((totalTarget - sum) * factor) / factor;
  if (Math.abs(drift) < 1 / factor / 2) return out;

  // The residue must not push a material through its own ceiling — or below its
  // own floor, which would take an ingredient that was counted out of the count
  // — so the candidates are those with room for it, largest first, where a
  // hundredth of a percent is least visible.
  const target = Object.entries(out)
    .filter(([key, v]) => !lockedSet.has(key)
      && v + drift >= (floors[key] ?? 0) - 1e-9
      && v + drift >= 0
      && v + drift <= (headroom[key] ?? totalTarget) + 1e-9)
    .sort((a, b) => b[1] - a[1])[0];
  if (target) out[target[0]] = Math.round((target[1] + drift) * factor) / factor;
  return out;
}

/**
 * Which limits the answer is sitting on.
 *
 * The binding constraints are the whole explanation of a least-cost blend: the
 * price is what it is *because* protein is at its floor and rape is at its
 * ceiling. Without them the solver is a black box that emits percentages.
 */
function bindingConstraints(values, book, targets, constrained, margin = 0) {
  // The answer sits at limit + margin rather than exactly on the limit, and the
  // inclusions are rounded on top of that. Both have to be inside the window or
  // the explanation comes back empty for a blend that is plainly pinned.
  const tolerance = margin + 0.01;
  const analysis = blendMath.nutrients(values, book);
  const out = [];
  for (const key of constrained) {
    const value = analysis.values[key];
    if (value === undefined) continue;
    const min = blendMath.toNumber(targets[key]?.min);
    const max = blendMath.toNumber(targets[key]?.max);
    if (min !== null && Math.abs(value - min) <= tolerance) out.push({ key, side: 'min', value, limit: min });
    else if (max !== null && Math.abs(value - max) <= tolerance) out.push({ key, side: 'max', value, limit: max });
  }
  return out;
}

export default solveBlend;


/**
 * The smallest change to an existing blend that meets a spec.
 *
 * "Take Chapple's November blend and make it 18% protein" is not a request for
 * the cheapest 18% blend — it is a request for THAT blend, moved as little as
 * possible. Solving it as least-cost answers a different question and hands back
 * a recipe the customer would not recognise.
 *
 * So the objective changes and nothing else does. Each material becomes a pair
 * of non-negative variables, up and down, with its inclusion expressed as
 * base + up - down; minimising the sum of up and down minimises total absolute
 * movement from the starting blend. The spec, the total and the bounds are the
 * same constraints as always, rewritten in terms of the same expression.
 *
 * That is a linear objective over linear constraints, so it goes through the
 * same simplex — no heuristic, and "no blend of these materials meets the spec
 * without adding something new" still comes back as a proof.
 *
 * The ingredient rules and the cost ceiling apply here exactly as they do to
 * solveBlend, through the same search — minimising movement instead of cost.
 */
export function solveClosestToBase({
  book,
  baseValues,
  candidates,
  columnKey,
  spec = null,
  bounds = {},
  totalTarget = 100,
  minIngredients = null,
  maxIngredients = null,
  costMax = null,
  minInclusion = null,
  differentFrom = [],
  limits = {}
}) {
  const targets = (spec && spec.targets) || {};
  const constrained = Object.entries(targets)
    .filter(([, t]) => blendMath.toNumber(t?.min) !== null || blendMath.toNumber(t?.max) !== null)
    .map(([key]) => key);

  const minCount = positiveInt(minIngredients);
  const maxCount = positiveInt(maxIngredients);
  const ceiling = positiveNumber(costMax);
  const cuts = (differentFrom || []).filter((c) => Array.isArray(c) && c.length);
  const statedFloor = statedMinimum(minInclusion);
  const rules = {
    minIngredients: minCount, maxIngredients: maxCount, costMax: ceiling,
    minInclusion: statedFloor, differentFrom: cuts
  };
  // Whether in-or-out is a decision, and the least "in" can be — as solveBlend.
  const counting = !!(minCount || maxCount);
  const floored = counting || statedFloor !== null;
  const floorAt = Math.max(INCLUSION_FLOOR, statedFloor ?? 0);

  const keys = (candidates && candidates.length ? candidates : Object.keys(baseValues || {}))
    .filter((k) => book.materials.has(k));

  const usable = [];
  const excluded = [];
  for (const key of keys) {
    const material = book.materials.get(key);
    const price = material.costs[columnKey];
    if (price === undefined || price === null) {
      excluded.push({ key, name: material.name, reason: 'no-price', detail: 'No price in this column' });
      continue;
    }
    const missing = constrained.filter((c) => material.nutrients[c] === undefined);
    if (missing.length) {
      excluded.push({ key, name: material.name, reason: 'no-nutrient',
                      detail: `No figure for ${missing.join(', ')}` });
      continue;
    }
    usable.push({ key, material, price, base: blendMath.toNumber(baseValues[key]) || 0 });
  }
  if (!usable.length) {
    return { status: 'no-candidates', values: null, excluded, rules,
             message: 'Nothing usable to work from — the base blend\'s materials are unpriced or unmeasured.' };
  }

  const n = usable.length;
  // Variables: [up_0..up_n-1, down_0..down_n-1]. x_i = base_i + up_i - down_i.
  const coeffOf = (perMaterial) => {
    const row = new Array(n * 2).fill(0);
    for (let i = 0; i < n; i += 1) {
      row[i] = perMaterial[i];
      row[n + i] = -perMaterial[i];
    }
    return row;
  };
  const baseSum = (perMaterial) =>
    usable.reduce((sum, u, i) => sum + perMaterial[i] * u.base, 0);

  const hiOf = usable.map((u) => {
    const rawHi = blendMath.toNumber(bounds[u.key]?.max);
    return rawHi === null ? totalTarget : rawHi;
  });
  // As solveBlend: a material with its own lower bound is in regardless, so the
  // floor applies to it directly, up to its own ceiling.
  const loOf = usable.map((u, i) => {
    const own = Math.max(0, blendMath.toNumber(bounds[u.key]?.min) ?? 0);
    return floored && own > EPS ? Math.min(Math.max(own, floorAt), hiOf[i]) : own;
  });

  // Base materials that could not be solved with are restored afterwards at
  // their own figure (see below), so they are in the blend whatever happens.
  const unusable = new Set(excluded.map((e) => e.key));
  let restoredCount = 0;
  let restoredCost = 0;
  for (const [key, value] of Object.entries(baseValues || {})) {
    const v = blendMath.toNumber(value) || 0;
    if (!unusable.has(key) || v <= 0) continue;
    restoredCount += 1;
    const price = blendMath.toNumber(book.materials.get(key)?.costs?.[columnKey]);
    if (price !== null) restoredCost += (v * price) / 100;
  }

  /** The movement LP with per-material bounds, as {status, x:{key:value}, moved}. */
  const solveWith = (lo, hi) => {
    const A = [];
    const b = [];
    const ops = [];

    // Total.
    const ones = new Array(n).fill(1);
    A.push(coeffOf(ones)); b.push(totalTarget - baseSum(ones)); ops.push('=');

    // Bounds. Down cannot take a material below its floor, up cannot take it
    // above its ceiling — expressed on base + up - down like everything else.
    for (let i = 0; i < n; i += 1) {
      const only = new Array(n).fill(0); only[i] = 1;
      A.push(coeffOf(only)); b.push(hi[i] - usable[i].base); ops.push('<=');
      A.push(coeffOf(only)); b.push(lo[i] - usable[i].base); ops.push('>=');
    }

    // Nutrients, with the same /100 the rest of the maths uses.
    for (const c of constrained) {
      const per = usable.map((u) => (u.material.nutrients[c] || 0) / 100);
      const min = blendMath.toNumber(targets[c]?.min);
      const max = blendMath.toNumber(targets[c]?.max);
      if (min !== null) { A.push(coeffOf(per)); b.push(min - baseSum(per)); ops.push('>='); }
      if (max !== null) { A.push(coeffOf(per)); b.push(max - baseSum(per)); ops.push('<='); }
    }

    // The ceiling. Rounded to 2dp afterwards without a margin loop, so it keeps
    // a few pence of headroom from the start.
    if (ceiling !== null) {
      const perTonne = usable.map((u) => u.price / 100);
      A.push(coeffOf(perTonne));
      b.push(ceiling - 0.05 - restoredCost - baseSum(perTonne));
      ops.push('<=');
    }

    // Minimise total movement: every up and every down costs the same.
    const objective = new Array(n * 2).fill(1);
    const result = solveLP(objective, A, b, ops);
    if (result.status !== 'optimal') return { status: result.status };
    const x = {};
    usable.forEach((u, i) => { x[u.key] = u.base + result.x[i] - result.x[n + i]; });
    return { status: 'optimal', x, moved: result.objective };
  };

  let lo = loOf;
  let hi = hiOf;
  let search = null;

  if (floored || cuts.length) {
    const indexOf = new Map(usable.map((u, i) => [u.key, i]));
    // Uncapped, as in solveBlend: a material that cannot reach the floor is out.
    const floorOf = (key) => {
      if (!floored) return 0;
      const i = indexOf.get(key);
      return i === undefined ? 0 : Math.max(loOf[i], floorAt);
    };
    const boundsFor = (forcedIn, forcedOut) => ({
      lo: usable.map((u, i) => (forcedOut.has(u.key) ? 0
        : forcedIn.has(u.key) ? Math.max(loOf[i], floorOf(u.key)) : loOf[i])),
      hi: usable.map((u, i) => (forcedOut.has(u.key) ? 0 : hiOf[i]))
    });

    const ms = limits.ms ?? SEARCH_LIMITS.ms;
    search = searchSupport({
      keys: usable.map((u) => u.key),
      solve: ({ forcedIn, forcedOut }) => {
        const bnd = boundsFor(forcedIn, forcedOut);
        const r = solveWith(bnd.lo, bnd.hi);
        return r.status === 'optimal' ? { status: 'optimal', x: r.x, cost: r.moved } : r;
      },
      mandatory: usable.filter((u, i) => loOf[i] > EPS).map((u) => u.key),
      fixedCount: restoredCount,
      minCount,
      maxCount,
      floorOf,
      cuts,
      nodeLimit: limits.nodes ?? SEARCH_LIMITS.nodes,
      deadline: Math.min(limits.deadline ?? Infinity, Date.now() + ms)
    });

    if (search.status !== 'optimal') {
      const refusal = ruleRefusal(search.status, rules, search.nodes,
        'This blend cannot reach that spec using only the materials it already contains. Allow other materials, or loosen the figure.');
      return { ...refusal, values: null, excluded, rules, nodes: search.nodes };
    }
    const support = new Set(search.best.support);
    const forcedIn = new Set([...support]);
    const forcedOut = new Set(usable.map((u) => u.key).filter((k) => !support.has(k)));
    ({ lo, hi } = boundsFor(forcedIn, forcedOut));
  }

  const solvedLP = solveWith(lo, hi);

  if (solvedLP.status !== 'optimal') {
    return {
      status: solvedLP.status === 'infeasible' ? 'infeasible' : solvedLP.status,
      values: null, excluded, rules,
      message: solvedLP.status === 'infeasible'
        ? (ceiling !== null
          ? `This blend cannot reach that spec under £${ceiling.toFixed(2)}/T using only these materials. Allow other materials, raise the ceiling, or loosen the figure.`
          : 'This blend cannot reach that spec using only the materials it already contains. Allow other materials, or loosen the figure.')
        : 'The solver could not settle on an answer.'
    };
  }

  const solved = {};
  const factor = 10 ** DECIMALS;
  usable.forEach((u) => {
    solved[u.key] = Math.max(0, Math.round(solvedLP.x[u.key] * factor) / factor);
  });
  /*
   * A base material the solver could not use keeps its original figure rather
   * than silently vanishing — but ONLY when it was unusable, not when the
   * caller deliberately left it out.
   *
   * Those are two different absences and this used to conflate them: anything
   * in baseValues but not in `solved` was restored, so a material the caller
   * excluded came back at its original inclusion. "Take this blend but replace
   * the beans" returned the beans, at 25%, on top of a recipe already summing
   * to 100 — a 125% blend that ignored the only instruction given.
   *
   * `excluded` holds exactly the candidates dropped for being unpriced or
   * unmeasured, which is the case the restore is for.
   */
  for (const [key, value] of Object.entries(baseValues || {})) {
    if (!(key in solved) && unusable.has(key) && (blendMath.toNumber(value) || 0) > 0) {
      solved[key] = blendMath.toNumber(value);
    }
  }
  for (const key of Object.keys(solved)) {
    if ((blendMath.toNumber(solved[key]) || 0) <= 0) delete solved[key];
  }

  // Over every key on EITHER side: a material dropped to nothing has moved by
  // its full inclusion, and counting only what survives would report the
  // largest change of all as no change.
  const movedKeys = new Set([...Object.keys(solved), ...Object.keys(baseValues || {})]);
  const moved = [...movedKeys].reduce((sum, k) =>
    sum + Math.abs((solved[k] || 0) - (blendMath.toNumber(baseValues[k]) || 0)), 0);

  return {
    status: 'optimal',
    values: solved,
    excluded,
    movedTotal: Math.round(moved * 100) / 100,
    cost: blendMath.cost(solved, book, columnKey).cost,
    previousCost: blendMath.cost(baseValues, book, columnKey).cost,
    nutrients: blendMath.nutrients(solved, book).values,
    rules,
    proved: search ? search.proved : true,
    // In percentage points moved, the objective here, not £.
    searchGap: search && !search.proved && search.lowerBound !== null
      ? Math.max(0, moved - search.lowerBound) : 0,
    nodes: search ? search.nodes : 0,
    ingredients: countIngredients(solved)
  };
}
